/**
 * Field Mappings Tab
 * 
 * Manage field mappings between external APIs and IKU format
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Transform as TransformIcon,
  ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';

import apiManagerApi from './api';

interface FieldMapping {
  mapping_id: string;
  api_id: string;
  endpoint_id: string;
  template_id: string;
  mappings: Array<{
    external_field: string;
    iku_field: string;
    transform?: string;
    unit_conversion?: {
      from: string;
      to: string;
    };
    confirmed: boolean;
    confidence?: number;
  }>;
  created_at: string;
}

export default function MappingsTab() {
  const [mappings, setMappings] = useState<FieldMapping[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadMappings();
  }, []);

  const loadMappings = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiManagerApi.listMappings();
      setMappings(response.data.mappings || []);
    } catch (err: any) {
      console.error('Failed to load mappings:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load mappings');
      setMappings([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteMapping = async (mappingId: string) => {
    if (!confirm('Are you sure you want to delete this mapping?')) return;

    try {
      await apiManagerApi.deleteMapping(mappingId);
      loadMappings();
    } catch (err: any) {
      console.error('Failed to delete mapping:', err);
      setError(err.response?.data?.error || err.message || 'Failed to delete mapping');
    }
  };

  const handleConfirmMapping = async (mappingId: string) => {
    try {
      await apiManagerApi.confirmMapping(mappingId);
      loadMappings();
    } catch (err: any) {
      console.error('Failed to confirm mapping:', err);
      setError(err.response?.data?.error || err.message || 'Failed to confirm mapping');
    }
  };

  return (
    <Box sx={{ px: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">
          Field Mappings
        </Typography>
        <Tooltip title="Refresh list">
          <IconButton onClick={loadMappings} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Empty State */}
      {!loading && mappings.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <TransformIcon sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No Field Mappings
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Field mappings transform external API data into IKU format with unit conversions
          </Typography>
        </Box>
      )}

      {/* Mappings Cards */}
      {!loading && mappings.length > 0 && (
        <Grid container spacing={3}>
          {mappings.map((mapping) => (
            <Grid item xs={12} key={mapping.mapping_id}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                    <Box>
                      <Typography variant="h6" gutterBottom>
                        {mapping.api_id} - {mapping.endpoint_id}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Template: {mapping.template_id}
                      </Typography>
                    </Box>
                    <Box>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteMapping(mapping.mapping_id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </Box>

                  <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>External Field</TableCell>
                          <TableCell align="center">→</TableCell>
                          <TableCell>IKU Field</TableCell>
                          <TableCell>Transform</TableCell>
                          <TableCell>Unit Conversion</TableCell>
                          <TableCell align="center">Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {mapping.mappings.map((field, idx) => (
                          <TableRow key={idx}>
                            <TableCell>
                              <code>{field.external_field}</code>
                            </TableCell>
                            <TableCell align="center">
                              <ArrowForwardIcon fontSize="small" color="action" />
                            </TableCell>
                            <TableCell>
                              <code>{field.iku_field}</code>
                            </TableCell>
                            <TableCell>
                              {field.transform ? (
                                <Chip label={field.transform} size="small" variant="outlined" />
                              ) : (
                                '-'
                              )}
                            </TableCell>
                            <TableCell>
                              {field.unit_conversion ? (
                                <Chip
                                  label={`${field.unit_conversion.from} → ${field.unit_conversion.to}`}
                                  size="small"
                                  color="info"
                                />
                              ) : (
                                '-'
                              )}
                            </TableCell>
                            <TableCell align="center">
                              {field.confirmed ? (
                                <Tooltip title="Confirmed">
                                  <CheckCircleIcon color="success" fontSize="small" />
                                </Tooltip>
                              ) : (
                                <Chip
                                  label={`${Math.round((field.confidence || 0) * 100)}%`}
                                  size="small"
                                  color="warning"
                                />
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {mapping.mappings.some(m => !m.confirmed) && (
                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<CheckCircleIcon />}
                        onClick={() => handleConfirmMapping(mapping.mapping_id)}
                      >
                        Confirm All Mappings
                      </Button>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
}

